<template>
  <div class="file-list-table">
    <div v-if="error" class="alert alert-danger rounded-3 shadow-sm">
      {{ error }}
    </div>
    
    <div v-if="isLoading" class="loading-state">
      <div class="spinner-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <p>Loading your files...</p>
    </div>
    
    <div v-else-if="files.length === 0" class="empty-state">
      <div class="empty-state-icon">
        <i class="bi bi-folder2-open"></i>
      </div>
      <h3>No files yet</h3>
      <p>Upload a file to get started</p>
    </div>
    
    <div v-else class="table-responsive">
      <table class="table table-hover">
        <thead>
          <tr>
            <th>File</th>
            <th class="d-none d-md-table-cell">Size</th>
            <th class="d-none d-lg-table-cell">Uploaded</th>
            <th class="d-none d-lg-table-cell">Expires</th>
            <th class="actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in files" :key="file.file_id">
            <td>
              <div class="file-item">
                <div class="file-icon">
                  <i :class="getFileIcon(file.content_type)"></i>
                </div>
                <div class="file-details">
                  <div class="file-name" :title="file.name">{{ file.name }}</div>
                  <small class="file-meta d-md-none">{{ formatFileSize(file.size_bytes) }}</small>
                </div>
              </div>
            </td>
            <td class="d-none d-md-table-cell">{{ formatFileSize(file.size_bytes) }}</td>
            <td class="d-none d-lg-table-cell">{{ formatRelativeTime(file.uploaded) }}</td>
            <td class="d-none d-lg-table-cell">{{ formatRelativeTime(file.expires) }}</td>
            <td class="actions-col">
              <div class="action-buttons">
                <router-link :to="{ name: 'FileDetails', params: { id: file.file_id } }" class="btn btn-sm btn-outline-primary">
                  <i class="bi bi-eye"></i> <span class="d-none d-md-inline">View</span>
                </router-link>
                <button v-if="isAdmin" class="btn btn-sm btn-outline-danger" @click="confirmDelete(file.file_id)">
                  <i class="bi bi-trash"></i> <span class="d-none d-md-inline">Delete</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="isAdmin" id="deleteModal" class="modal fade" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Deletion</h5>
            <button type="button" class="btn-close" @click="closeConfirmationModal"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete this file?</p>
            <p class="text-muted small">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="closeConfirmationModal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="deleteFileConfirmed">
              <i class="bi bi-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { Modal } from 'bootstrap';
import { formatFileSize, formatTimestamp, formatRelativeTime, getFileIconClass } from '@/utils';

export default defineComponent({
  name: 'FileListTable',
  props: {
    isAdmin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      modal: null,
      selectedFileId: null,
    };
  },
  computed: {
    ...mapGetters('files', ['stateFiles', 'stateIsLoading', 'stateFileError']),
    files() {
      return this.stateFiles || [];
    },
    isLoading() {
      return this.stateIsLoading;
    },
    error() {
      return this.stateFileError;
    },
  },
  created() {
    this.fetchFiles();
  },
  methods: {
    ...mapActions('files', ['getFiles', 'deleteFile']),
    formatFileSize,
    formatTimestamp,
    formatRelativeTime,
    fetchFiles() {
      this.getFiles();
    },
    getFileIcon(contentType) {
      return getFileIconClass(contentType);
    },
    confirmDelete(fileId) {
      this.selectedFileId = fileId;
      this.modal = new Modal(document.getElementById('deleteModal'));
      this.modal.show();
    },
    closeConfirmationModal() {
      if (this.modal) {
        this.modal.hide();
      }
    },
    async deleteFileConfirmed() {
      try {
        await this.deleteFile(this.selectedFileId);
        this.fetchFiles();
      } catch (error) {
        console.error('Error deleting file:', error);
      }
      this.closeConfirmationModal();
    },
  },
});
</script>

<style scoped>
.file-list-table {
  margin-bottom: 2rem;
}

/* Loading and empty states */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.spinner-container {
  margin-bottom: 1rem;
}

.empty-state-icon {
  font-size: 3rem;
  color: var(--border-strong);
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--muted);
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: var(--faint);
  margin-bottom: 0;
}

/* Table styling */
.table {
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
}

.table thead th {
  background-color: var(--surface);
  color: var(--faint);
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
}

.table tbody td {
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}

.table tbody tr:last-child td {
  border-bottom: none;
}

.table tbody tr {
  transition: background-color 0.2s;
}

.table-hover tbody tr:hover {
  background-color: var(--surface);
}

/* File item styling */
.file-item {
  display: flex;
  align-items: center;
}

.file-icon {
  flex: 0 0 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--accent-soft);
  border-radius: var(--radius);
  margin-right: 0.75rem;
}

.file-icon i {
  color: var(--accent);
  font-size: 1.25rem;
}

.file-details {
  overflow: hidden;
}

.file-name {
  font-weight: 500;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.file-meta {
  display: block;
  color: var(--faint);
  font-size: 0.75rem;
}

/* Action buttons */
.actions-col {
  width: 120px;
  text-align: right;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  border-radius: 4px;
}

/* Modal styling */
.modal-content {
  border-radius: 12px;
  border: none;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
}

.modal-header {
  border-bottom: 1px solid var(--border);
  padding: 0.75rem 1rem;
}

.modal-footer {
  border-top: 1px solid var(--border);
  padding: 0.75rem 1rem;
}

.modal-body {
  padding: 1rem;
}

.modal-title {
  font-weight: 600;
  color: var(--text);
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
  .table td, 
  .table th {
    padding: 0.75rem 0.5rem;
  }
  
  .file-name {
    max-width: 150px;
  }
  
  .actions-col {
    width: 90px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .action-buttons .btn {
    padding: 0.25rem;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  
  .action-buttons .btn i {
    margin: 0;
  }
}
</style>